import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Download } from 'lucide-react';
import { paymentService } from '../../services/paymentService';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import DataTable from '../../components/common/DataTable';
import Select from '../../components/common/Select';
import StatusBadge from '../../components/common/StatusBadge';
import Badge from '../../components/common/Badge';
import EmptyState from '../../components/common/EmptyState';
import Skeleton from '../../components/common/Skeleton';
import { formatPrice } from '../../utils/priceCalculator';
import { formatDate, formatDateTime } from '../../utils/formatRelativeTime';
import { exportCsv } from '../../utils/exportCsv';

const PAGE_SIZE = 20;

const isOverdue = (dueDate, status) =>
  status === 'pending' && dueDate && new Date(dueDate) < new Date();

const ResellerPayments = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        paymentService.getAll({ status: filter || undefined, page, limit: PAGE_SIZE }),
        paymentService.getStats(),
      ]);
      setRows(list?.rows || []);
      setTotal(list?.count || 0);
      setStats(s);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      key: 'invoiceNumber',
      header: 'Fatura',
      render: (p) => (
        <div>
          <p className="font-semibold text-app-text">{p.invoiceNumber || `#${p.id}`}</p>
          {p.description && (
            <p className="text-xs text-app-text-secondary truncate max-w-xs">{p.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'orderId',
      header: 'Pedido',
      render: (p) =>
        p.orderId ? (
          <button
            type="button"
            className="text-primary hover:underline text-sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/revendedor/painel/pedidos/${p.orderId}`);
            }}
          >
            #{p.orderId}
          </button>
        ) : (
          <span className="text-xs text-app-text-secondary">—</span>
        ),
    },
    {
      key: 'dueDate',
      header: 'Vence em',
      render: (p) => {
        if (p.paidAt) {
          return <span className="text-xs text-success">Pago {formatDate(p.paidAt)}</span>;
        }
        if (!p.dueDate) return <span className="text-xs text-app-text-secondary">—</span>;
        const overdue = isOverdue(p.dueDate, p.status);
        return (
          <div className="flex items-center gap-1.5">
            <span className={`text-sm ${overdue ? 'text-error font-semibold' : ''}`}>
              {formatDate(p.dueDate)}
            </span>
            {overdue && <Badge color="error" size="sm" variant="soft">Atrasado</Badge>}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (p) => <StatusBadge domain="payment" status={p.status} />,
    },
    {
      key: 'amount',
      header: 'Valor',
      align: 'right',
      render: (p) => <span className="font-bold text-primary">{formatPrice(parseFloat(p.amount))}</span>,
    },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold text-app-text">Meus pagamentos</h1>
        <div className="flex gap-2">
          <Button
            variant="subtle"
            leftIcon={<Download size={16} />}
            onClick={() =>
              exportCsv({
                filename: 'meus-pagamentos.csv',
                columns: [
                  { key: 'id', header: 'ID' },
                  { key: 'invoiceNumber', header: 'Fatura' },
                  { key: 'orderId', header: 'Pedido' },
                  { key: 'amount', header: 'Valor' },
                  { key: 'status', header: 'Status' },
                  { key: 'dueDate', header: 'Vencimento', format: formatDateTime },
                  { key: 'paidAt', header: 'Pago em', format: formatDateTime },
                ],
                rows,
              })
            }
            disabled={!rows.length}
          >
            Exportar CSV
          </Button>
          <Select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos' },
              { value: 'pending', label: 'Pendentes' },
              { value: 'paid', label: 'Pagos' },
              { value: 'overdue', label: 'Vencidos' },
              { value: 'cancelled', label: 'Cancelados' },
            ]}
          />
        </div>
      </div>

      {stats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="p-4">
            <p className="text-xs text-app-text-secondary">Total faturas</p>
            <p className="text-2xl font-extrabold text-app-text">{stats.total || 0}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-app-text-secondary">Pendente</p>
            <p className="text-xl font-extrabold text-warning">{formatPrice(stats.totalPending || 0)}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-app-text-secondary">Pago</p>
            <p className="text-xl font-extrabold text-success">{formatPrice(stats.totalPaid || 0)}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-app-text-secondary">Vencido</p>
            <p className="text-xl font-extrabold text-error">{formatPrice(stats.totalOverdue || 0)}</p>
          </Card>
        </div>
      ) : loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="block" height={80} />
          ))}
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        onRowClick={(p) => navigate(`/revendedor/painel/pagamentos/${p.id}`)}
        empty={
          <EmptyState
            icon={DollarSign}
            title="Nenhum pagamento"
            description={filter ? 'Ajuste o filtro.' : 'Quando gerar um pedido, as cobranças aparecem aqui.'}
          />
        }
      />
    </div>
  );
};

export default ResellerPayments;
